import { completionKeymap } from '@codemirror/autocomplete'
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands'
import type { Compartment, Extension } from '@codemirror/state'
import { placeholder as cmPlaceholder, EditorView, keymap } from '@codemirror/view'

import { templateVariableAutocomplete } from './template-variable-autocomplete.extension'
import { templateVariableHighlightPlugin } from './template-variable-highlight.extension'

interface EditorExtensionsParams {
  placeholder: string
  minHeight: string
  isDisabled: boolean
  editableCompartment: Compartment
  updateListener: Extension
}

const createEditorTheme = (minHeight: string) =>
  EditorView.theme({
    '&': { minHeight },
    '.cm-scroller': { minHeight },
    '.cm-tooltip': {
      border: '1px solid #e2e8f0',
      borderRadius: '0.375rem',
      backgroundColor: '#ffffff',
      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
      zIndex: '50',
    },
    '.cm-tooltip-autocomplete > ul': {
      fontFamily: 'inherit',
      fontSize: '0.875rem',
      maxHeight: '16rem',
    },
    '.cm-tooltip-autocomplete > ul > li': {
      padding: '0.375rem 0.5rem',
      cursor: 'pointer',
    },
    '.cm-tooltip-autocomplete > ul > li[aria-selected]': {
      backgroundColor: '#f1f5f9',
      color: '#0f172a',
    },
    '.cm-completionInfo': {
      padding: '0.375rem 0.5rem',
      borderLeft: '1px solid #e2e8f0',
      backgroundColor: '#ffffff',
      color: '#64748b',
      fontSize: '0.8125rem',
    },
  })

/**
 * Creates the full set of CodeMirror 6 extensions for the template variable editor.
 */
export const createEditorExtensions = ({
  placeholder,
  minHeight,
  isDisabled,
  editableCompartment,
  updateListener,
}: EditorExtensionsParams): Extension[] => [
  keymap.of([...completionKeymap, ...defaultKeymap, ...historyKeymap]),
  history(),
  cmPlaceholder(placeholder),
  templateVariableHighlightPlugin,
  templateVariableAutocomplete,
  createEditorTheme(minHeight),
  EditorView.lineWrapping,
  editableCompartment.of(EditorView.editable.of(!isDisabled)),
  updateListener,
]
